/**
 * Main Application Component
 * 
 * Root component of the MacSafety web application that serves as the central hub
 * for application-wide functionality, user authentication, navigation, and UI state
 * management. This component acts as a bridge between various services and the user
 * interface.
 * 
 * Key Responsibilities:
 * - Application initialization and service setup
 * - User authentication state management (login/logout)
 * - Route tracking and navigation state management
 * - Screen size detection and responsive behavior
 * - Icon registration for the entire application
 * - Menu navigation and user interface state
 * - Development mode utilities and debugging
 * 
 * Service Integration:
 * - EntraService: Microsoft Entra ID authentication and user management
 * - ScreenSizeService: Responsive design and device detection
 * - FormModelService: Form metadata and configuration
 * - Router: Navigation and route state management
 * - TitleService: Browser title management
 * 
 * Authentication Flow:
 * - Manages login/logout user interactions
 * - Bridges user clicks to EntraService authentication methods
 * - Maintains user state and admin privileges
 * - Handles authentication-dependent UI rendering
 * 
 * Route Management:
 * - Tracks current route, title, and icon for dynamic header updates
 * - Updates browser title based on current route
 * - Manages form route detection and navigation state
 * - Provides route information to child components
 * 
 * Responsive Design:
 * - Initializes and manages screen size detection
 * - Provides responsive behavior signals to templates
 * - Handles mobile vs desktop UI adaptations
 * - Manages device-specific icon and layout decisions
 * 
 * Development Features:
 * - Conditionally renders LoadAllIonicComponent for Ionic preloading
 * - Provides debugging access to component state
 * - Manages development-only functionality
 * 
 * @author MacSafety Development Team
 * @version 1.0
 */

import {
  Component,
  isDevMode,
  signal,
  computed,
  effect,
  ChangeDetectionStrategy,
  OnDestroy,
} from '@angular/core';
import {
  RouterOutlet,
  Router,
  NavigationEnd,
  ActivatedRoute,
} from '@angular/router';
import { Title } from '@angular/platform-browser';
import { environment } from '../environments/environment';
import { addIcons } from 'ionicons';
import { filter, map, mergeMap, Subscription } from 'rxjs';
import {
  removeCircleOutline,
  contractOutline,
  documentOutline,
  videocamOutline,
  cloudUploadOutline,
  manOutline,
  fingerPrintOutline,
  radioOutline,
  handLeftOutline,
  analyticsOutline,
  documentsOutline,
  pencilOutline,
  cameraOutline,
  businessOutline,
  briefcaseOutline,
  alertCircleOutline,
  medkitOutline,
  informationCircleOutline,
  fileTrayFullOutline,
  listOutline,
  addCircleOutline,
  warningOutline,
  clipboardOutline,
  constructOutline,
  documentTextOutline,
  personOutline,
  chevronForwardOutline,
  chevronBackOutline,
  chevronUpOutline, 
  checkmarkOutline,
  buildOutline,
  serverOutline,
  cellularOutline,
  checkboxOutline,
  hardwareChipOutline,
  telescopeOutline,
  rocketOutline,
  statsChartOutline,
  enterOutline,
  calendarOutline,
  flameOutline,
  shieldOutline,
  listCircleOutline,
  carOutline,
  flashOutline,
  shieldCheckmarkOutline,
  speedometerOutline,
  cogOutline,
  swapVerticalOutline,
  warning,
  alertCircle,
  hourglassOutline,
  closeOutline,
  searchOutline,
  closeCircleOutline,
  filterOutline,
  close,
  optionsOutline,
  logInOutline,
  logOutOutline,
  printOutline,
  arrowUpOutline,
  arrowDownOutline,
  arrowForwardOutline,
  arrowBackOutline,
  ellipsisHorizontalOutline,
  ellipsisVerticalOutline,
  ellipsisVerticalCircleOutline,
  ellipsisHorizontal,
  chevronDownOutline,
  chevronDown,
  documentAttachOutline,
  refreshOutline,
  sendOutline,
  mailOutline,
  peopleOutline,
  checkmarkCircle,
  checkmarkCircleOutline,
  downloadOutline,
  trashOutline,
  starOutline,
  star,
  attachOutline,
  playCircleOutline,
  imagesOutline,
  openOutline,
  imageOutline,
  appsOutline,
  ellipsisVertical,
  albumsOutline,
  chevronUp
  } from 'ionicons/icons';
import { ScreenSizeService } from './services/screen-size.service';
import { FormModelService } from './services/form-model.service';
import { FormMetadata } from './models/form-registry';
import { EntraService } from './services/entra.service';
import { User } from './models/User.interface';
import { FormService } from './services/form.service';
import { ModalController } from '@ionic/angular';
import { BulkResendModalComponent } from './components/forms/form-entries/bulk-resend-modal/bulk-resend-modal.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnDestroy {
  title = 'macsafety-web-app';
  isDevMode: boolean;
  // All forms from registry - consider using accessibleForms getter instead for filtered list
  forms: FormMetadata[] = [];

  // Route tracking properties - using signals for reactive updates
  currentRouteTitle = signal<string>('');
  currentRouteIcon = signal<string>('');
  currentRoutePath = signal<string>('');
  currentFormType = signal<string | undefined>(undefined);
  private routeSubscription?: Subscription;

  // Screen size tracking properties - using computed signals for better reactivity
  // These automatically update when the underlying service signals change
  isSmallScreen = computed(() => this.screenSizeService.isSmallScreen());
  isLargeScreen = computed(() => this.screenSizeService.isLargeScreen());
  isMobile = computed(() => this.screenSizeService.isMobileDevice());
  isPhone = computed(() => this.screenSizeService.isPhone());
  isTablet = computed(() => this.screenSizeService.isTablet());
  isDesktop = computed(() => this.screenSizeService.isComputer());

  // Form list state management
  public isFormListOpen = false;

  //Version info
  public appVersion: string = environment.version;

  // Cached computed values to prevent excessive re-computation
  // These signals provide reactive user state information
  private readonly _user = computed(() => this.entraService.user());

  // Cached values for template access with timeout-based refresh
  // Prevents excessive signal calls while maintaining reactivity
  private _cachedUser: User | null = null;
  private _lastUpdateTime = 0;
  private readonly _cacheTimeout = 100; // Cache for 100ms to reduce frequent calls

  constructor(
    private screenSizeService: ScreenSizeService,
    public router: Router,
    private formModelService: FormModelService,
    private entraService: EntraService,
    private activatedRoute: ActivatedRoute,
    private titleService: Title,
    private formService: FormService,
    private modalController: ModalController
  ) {
    // Register all Ionic icons used throughout the application
    // This ensures icons are available for immediate use in templates
    addIcons({
      albumsOutline,
      imageOutline,
      removeCircleOutline,
      contractOutline,
      imagesOutline,
      playCircleOutline,
      attachOutline,
      trashOutline,
      starOutline,
      star,
      documentOutline,
      videocamOutline,
      cloudUploadOutline,
      downloadOutline,
      checkmarkCircleOutline,
      checkmarkCircle,
      hourglassOutline,
      documentAttachOutline,
      refreshOutline,
      sendOutline,
      mailOutline,
      peopleOutline,
      optionsOutline,
      searchOutline,
      closeOutline,
      fingerPrintOutline,
      manOutline,
      radioOutline,
      handLeftOutline,
      analyticsOutline,
      documentsOutline,
      pencilOutline,
      shieldCheckmarkOutline,
      cameraOutline,
      businessOutline,
      fileTrayFullOutline,
      listOutline,
      clipboardOutline,
      addCircleOutline,
      constructOutline,
      documentTextOutline,
      personOutline,
      warningOutline,
      chevronForwardOutline,
      chevronBackOutline,
      chevronDownOutline,
      chevronUpOutline,
      checkmarkOutline,
      buildOutline,
      serverOutline,
      cellularOutline,
      checkboxOutline,
      hardwareChipOutline,
      telescopeOutline,
      rocketOutline,
      statsChartOutline,
      enterOutline,
      calendarOutline,
      briefcaseOutline,
      alertCircleOutline,
      medkitOutline,
      informationCircleOutline,
      flameOutline,
      flashOutline,
      shieldOutline,
      carOutline,
      listCircleOutline,
      speedometerOutline,
      cogOutline,
      swapVerticalOutline,
      warning,
      alertCircle,
      filterOutline,
      closeCircleOutline,
      close,
      logInOutline,
      logOutOutline,
      printOutline,
      arrowUpOutline,
      arrowDownOutline,
      arrowForwardOutline,
      arrowBackOutline,
      ellipsisHorizontal,
      ellipsisHorizontalOutline,
      ellipsisVerticalOutline,
      ellipsisVerticalCircleOutline,
      chevronDown,
      openOutline,
      appsOutline,
      ellipsisVertical,
    });
    
    // Initialize form metadata and application state
    this.forms = this.formModelService.getAllFormMetadata();
    this.isDevMode = !environment.production;



     // Set up effect to update cached values when user state changes
    // This ensures UI stays in sync with authentication state
    effect(() => {
      const user = this._user();

      // Update cached values for template access
      this._cachedUser = user;
      this._lastUpdateTime = Date.now();

      if (user) {
        console.log(`User logged in: ${user.name}`);
        // Add analytics, notifications, etc.
      } else {
        console.log('User logged out');
        // Add cleanup logic, redirects, etc.
      }
    });
  }

  /**
   * Component initialization - sets up all application services and state tracking
   * Called once when the component is created
   */
  ngOnInit() {
    // Initialize screen size detection and responsive behavior
    this.screenSizeService.initialize();
    this.screenSizeService.setupScreenSizeListener();

    // Update cached values initially
    this.updateCachedValues();

    // Set up route tracking for dynamic header updates
    this.setupRouteTracking();

   

    // Make app component accessible for debugging (only in dev mode)
    if (isDevMode()) {
      (window as any).appComponent = this;
      console.log(
        'App component available as window.appComponent for debugging'
      );
    }
  }

  /**
   * Component cleanup - unsubscribe from route tracking to prevent memory leaks
   */
  ngOnDestroy() {
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }
  }

  //Open new tab for vehicle collision form
  public openVehicleCollisionForm(): void {
    this.formService.openExternalVehicleCollisionForm();
  }

  /**
   * Opens the admin-only bulk resend modal for resending form entry emails in bulk.
   * Backdrop dismiss is disabled so an in-progress run is not accidentally closed.
   */
  public async openBulkResendModal(): Promise<void> {
    const modal = await this.modalController.create({
      component: BulkResendModalComponent,
      backdropDismiss: false
    });
    await modal.present();
  }

  // ========================================
  // ROUTE TRACKING METHODS
  // ========================================

  /**
   * Sets up route tracking to monitor navigation changes and update UI state
   * Tracks current route, title, icon, and form type for dynamic header updates
   * Updates browser title and component signals when routes change
   */
  private setupRouteTracking(): void {
    this.routeSubscription = this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map((route) => {
          // Navigate to the deepest child route to get actual route data
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        mergeMap((route) => route.data)
      )
      .subscribe((data) => {
        const currentUrl = this.router.url;
        const title = data['title'] || 'MacSafety';
        const formType = data['formType'];
        const icon = data['titleIcon'] || null;
        
        // Update signals for reactive UI updates
        this.currentRouteTitle.set(title);
        this.currentRouteIcon.set(icon);
        this.currentRoutePath.set(currentUrl);
        this.currentFormType.set(formType);

        // Update browser title for better user experience
        this.titleService.setTitle(title);
      });
  }

  // Public getters for template access to route information
  get currentTitle(): string {
    return this.currentRouteTitle();
  }

  get currentPath(): string {
    return this.currentRoutePath();
  }

  get formType(): string | undefined {
    return this.currentFormType();
  }

  /**
   * Checks if the current route is a form route
   * Used for conditional UI rendering based on route type
   */
  isFormRoute(): boolean {
    return this.currentFormType() !== undefined;
  }

  // ========================================
  // SCREEN SIZE TRACKING METHODS
  // ========================================

  // Public getters for template access to screen size information
  // These provide reactive screen size data to templates
  get isSmallScreenValue(): boolean {
    return this.isSmallScreen();
  }

  get isMobileValue(): boolean {
    return this.isMobile();
  }

  get isPhoneValue(): boolean {
    return this.isPhone();
  }

  get isTabletValue(): boolean {
    return this.isTablet();
  }

  get isDesktopValue(): boolean {
    return this.isDesktop();
  }

  /**
   * Manually refreshes screen size tracking (useful for debugging)
   * Triggers recomputation of all screen size signals
   */
  public refreshScreenSizeTracking(): void {
    this.screenSizeService.updateScreenSize();
    // The computed signals will automatically update when the service signals change
  }

  // ========================================
  // USER STATE MANAGEMENT METHODS
  // ========================================

  /**
   * Gets the current user object with caching to reduce signal calls
   * Returns null if no user is logged in
   */
  get currentUser(): User | null {
    this.updateCachedValuesIfNeeded();
    return this._cachedUser;
  }

  /**
   * Checks if a user is currently logged in
   */
  get isUserLoggedIn(): boolean {
    return this.currentUser !== null;
  }

  /**
   * Gets the current user's name
   * Returns empty string if no user is logged in
   */
  get currentUserName(): string {
    return this.currentUser?.name ?? '';
  }

  /**
   * Gets the current user's email
   * Returns empty string if no user is logged in
   */
  get currentUserEmail(): string {
    return this.currentUser?.email ?? '';
  }

  /**
   * Checks if the current user has admin privileges
   */
  get userIsAdmin(): boolean {
    return this.currentUser?.isAdmin ?? false;
  }


  /**
   * Gets all forms that the current user has access to
   * Uses FormModelService to check access control rules
   */
  get accessibleForms(): FormMetadata[] {
    return this.formModelService.getAccessibleForms(this.currentUser);
  }

  /**
   * Updates cached values if the cache timeout has expired
   * Prevents excessive signal calls while maintaining reactivity
   */
  private updateCachedValuesIfNeeded(): void {
    const now = Date.now();
    if (now - this._lastUpdateTime > this._cacheTimeout) {
      this.updateCachedValues();
    }
  }

  /**
   * Updates cached user state values from signals
   * Called when cache expires or user state changes
   */
  private updateCachedValues(): void {
    this._cachedUser = this._user();
    this._lastUpdateTime = Date.now();
  }

  /**
   * Checks if the current route is a form route
   * Used for conditional UI rendering and navigation logic
   */
  isFormRouteActive(): boolean {
    const currentRoute = this.router.url;
    return currentRoute.startsWith('/forms/');
  }

  // ========================================
  // UI INTERACTION METHODS
  // ========================================

  /**
   * Opens the form submission accordion
   * Used for programmatic accordion control
   */
  openAccordion() {
    const accordion = document.querySelector(
      'ion-accordion[value="submit-form"]'
    );
    if (accordion) {
      (accordion as any).expanded = true;
    }
  }

  /**
   * Closes the form submission accordion
   * Used for programmatic accordion control
   */
  closeAccordion() {
    const accordion = document.querySelector(
      'ion-accordion[value="submit-form"]'
    );
    if (accordion) {
      (accordion as any).expanded = false;
    }
  }

  /**
   * Navigates to the forms home page
   * Used for logo clicks and navigation actions
   */
  navigateToHome() {
    this.router.navigate(['/forms']);
  }

  // ========================================
  // AUTHENTICATION METHODS
  // ========================================

  /**
   * Handles user login - bridges user interaction to EntraService
   * Clears localStorage and triggers Microsoft Entra ID authentication
   * Called when user clicks login button in the UI
   */
  public async login() {
    localStorage.clear();
    this.entraService.login();
  }

  /**
   * Handles user logout - bridges user interaction to EntraService
   * Clears localStorage and triggers Microsoft Entra ID logout
   * Called when user clicks logout button in the UI
   */
  public async logout() {
    localStorage.clear();
    this.entraService.logout();
  }
}